import { AdminLayout } from "@/components/layouts/admin-layout"
import { Badge } from "@/components/ui/badge"
import { MainContainer } from "@/components/ui/container"
import { DataTable } from "@/components/ui/data-table"
import type { AppliedSort, DataColumn } from "@/components/ui/data-table"
import { PageHeader } from "@/components/ui/headers"
import { t } from "@/lib/i18n"

type AdminEventRow = {
  id: number
  locationId: number | null
  action: string
  aggregateType: string | null
  aggregateId: number | null
  aggregate: string | null
  actorType: string | null
  actorId: number | null
  actor: string | null
  eventName: string
  createdAt: string
}

type Pagination = {
  page: number
  perPage: number
  totalCount: number
  totalPages: number
  hasPrevious: boolean
  hasNext: boolean
  previousPage: number | null
  nextPage: number | null
}

type Props = {
  events: AdminEventRow[]
  pagination: Pagination
  sort: AppliedSort
}

function actionLabel(action: string) {
  return action.replace(/_/g, " ")
}

const TABLE_PROPS = [ "events", "pagination", "sort" ]

const columns: DataColumn<AdminEventRow>[] = [
  {
    accessorKey: "id",
    header: "ID",
    // The event id is what a support thread quotes, so it is this table's
    // identity — and it is machine data, hence mono.
    field: { type: "title", mono: true },
  },
  {
    accessorKey: "createdAt",
    header: "Fecha",
    field: { type: "datetime" },
    sortKey: "created_at",
    mobile: "meta",
  },
  {
    accessorKey: "eventName",
    header: "Evento",
    sortKey: "action",
    cell: ({ row }) => (
      <div className="flex flex-col gap-1">
        <Badge variant="outline" className="w-fit capitalize">
          {actionLabel(row.original.action)}
        </Badge>
        <span className="text-muted-foreground text-xs">{row.original.eventName}</span>
      </div>
    ),
  },
  {
    accessorKey: "aggregate",
    header: t("ui.admin.events.headers.added"),
    field: { type: "code" },
    sortKey: "aggregate_type",
    mobile: "secondary",
  },
  {
    accessorKey: "actor",
    header: t("ui.admin.events.headers.actor"),
    cell: ({ row }) =>
      row.original.actor ? (
        <span className="text-xs">{row.original.actor}</span>
      ) : (
        <span className="text-muted-foreground text-xs">{t("ui.admin.events.actor.system")}</span>
      ),
  },
  {
    accessorKey: "locationId",
    header: t("ui.admin.events.headers.location"),
    field: { type: "number" },
  },
]

export default function AdminEventsIndex({ events, pagination, sort }: Props) {
  return (
    <AdminLayout>
      <MainContainer>
        <PageHeader
          title={t("ui.react.pages.admin.events.index.events")}
          description={`Mostrando ${events.length} de ${pagination.totalCount} eventos`}
        />

        <DataTable
          mode="server"
          url="/events"
          columns={columns}
          data={events}
          surface="card"
          sort={sort}
          pagination={pagination}
          perPageOptions={[ 25, 50, 100 ]}
          reloadOnly={TABLE_PROPS}
        />
      </MainContainer>
    </AdminLayout>
  )
}
